import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradiesOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: user?.full_name || '',
    tradie_type: 'individual'
  });

  // Redirect if already has tradies profile
  React.useEffect(() => {
    if (user?.has_tradies_profile) {
      navigate('/tradies/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/profile/enable-tradies`, formData);
      toast.success('GoTradies profile enabled successfully!');
      
      // Refresh user data
      const response = await axios.get(`${API}/auth/me`);
      // Update user context would happen automatically on next page load
      
      navigate('/tradies/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable GoTradies profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please log in to continue</h2>
          <button
            onClick={() => navigate('/login?redirect=/tradies/onboarding')}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 border">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Enable GoTradies</h1>
              <p className="text-muted-foreground text-lg">
                Set up your tradie profile to start receiving jobs and growing your business
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., ABC Plumbing Services"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This will be displayed on your profile
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Business Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tradie_type: 'individual' })}
                    className={`px-6 py-4 border-2 rounded-xl font-medium transition-all ${
                      formData.tradie_type === 'individual'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tradie_type: 'company' })}
                    className={`px-6 py-4 border-2 rounded-xl font-medium transition-all ${
                      formData.tradie_type === 'company'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Company
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold mb-2">What's Next?</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>✓ Complete your profile with more details</li>
                  <li>✓ Verify your ABN and licenses</li>
                  <li>✓ Create service listings</li>
                  <li>✓ Start receiving job requests</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.business_name}
                className="w-full bg-blue-600 text-white font-semibold py-4 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  'Enable GoTradies Profile'
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/tradies')}
                className="w-full px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Back to GoTradies
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradiesOnboarding;
